/**
 * PricingSection — three tier cards (Reader, Supporter, Creator).
 * Note the "?" placeholder in the Supporter price — this is deliberate in
 * the source copy (pricing is set per-journal by each creator).
 */

import { useTranslation } from 'react-i18next';

import {
  Section,
  Container,
  SectionHeader,
  SectionEyebrow,
  SectionTitle,
  SectionSub,
  TiersGrid,
  TierCard,
  TierBadge,
  TierNameLabel,
  TierPriceWrap,
  TierPriceCurrency,
  TierPriceAmount,
  TierPricePeriod,
  TierPerksList,
  TierPerkItem,
  TierCta,
  PricingNote,
} from '../../common/LandingPage.styled';
import { VellumProIconFlat } from '../../../../components/brand';

/** Renders a tier's perks list with the flat pro-icon check. */
function TierPerks({ perks }: { perks: string[] }) {
  return (
    <TierPerksList>
      {perks.map((p) => (
        <TierPerkItem key={p}>
          <span className="check">
            <VellumProIconFlat size={12} color="#D9D9D9" />
          </span>
          {p}
        </TierPerkItem>
      ))}
    </TierPerksList>
  );
}

export function PricingSection() {
  const { t } = useTranslation();

  const readerPerks = t('landing.pricing.tierReader.perks', { returnObjects: true }) as string[];
  const supporterPerks = t('landing.pricing.tierSupporter.perks', {
    returnObjects: true,
  }) as string[];
  const creatorPerks = t('landing.pricing.tierCreator.perks', { returnObjects: true }) as string[];

  return (
    <Section id="pricing">
      <Container>
        <SectionHeader>
          <SectionEyebrow>{t('landing.pricing.eyebrow')}</SectionEyebrow>
          <SectionTitle>{t('landing.pricing.title')}</SectionTitle>
          <SectionSub>{t('landing.pricing.sub')}</SectionSub>
        </SectionHeader>

        <TiersGrid>
          {/* Reader — free */}
          <TierCard>
            <TierNameLabel>{t('landing.pricing.tierReader.name')}</TierNameLabel>
            <TierPriceWrap>
              <TierPriceAmount>{t('landing.pricing.tierReader.price')}</TierPriceAmount>
            </TierPriceWrap>
            <TierPerks perks={readerPerks} />
            <TierCta to="/register">{t('common.getStarted')}</TierCta>
          </TierCard>

          {/* Supporter — featured */}
          <TierCard featured>
            <TierBadge>{t('landing.pricing.mostPopular')}</TierBadge>
            <TierNameLabel>{t('landing.pricing.tierSupporter.name')}</TierNameLabel>
            <TierPriceWrap>
              <TierPriceCurrency>$</TierPriceCurrency>
              <TierPriceAmount>?</TierPriceAmount>
              <TierPricePeriod> {t('landing.pricing.tierSupporter.priceNote')}</TierPricePeriod>
            </TierPriceWrap>
            <TierPerks perks={supporterPerks} />
            <TierCta to="/register" featured>
              {t('common.getStarted')}
            </TierCta>
          </TierCard>

          {/* Creator */}
          <TierCard>
            <TierNameLabel>{t('landing.pricing.tierCreator.name')}</TierNameLabel>
            <TierPriceWrap>
              <TierPriceAmount>{t('landing.pricing.tierCreator.price')}</TierPriceAmount>
            </TierPriceWrap>
            <TierPerks perks={creatorPerks} />
            <TierCta to="/register">{t('common.getStarted')}</TierCta>
          </TierCard>
        </TiersGrid>

        <PricingNote>{t('landing.pricing.note')}</PricingNote>
      </Container>
    </Section>
  );
}

export default PricingSection;
